import { useCallback, useRef } from 'react'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'
import { isLive } from 'tripPlanner/assistant/surfaceSnapshots/expiry'

function pruneExpiredEntries(accumulator: Map<string, TripPlannerCommittedStateEntry>) {
  accumulator.forEach((entry, key) => {
    if (!isLive(entry.expiresAt)) accumulator.delete(key)
  })
}

// Stable per-scope facts persist until expiry, explicit replacement or session reset.
// Never evict live facts to fit a publish limit: an incomplete canonical snapshot
// would lose searches from other stops. Backend admission bounds the full payload;
// publication beyond that boundary degrades without destroying this local memory.
export function useCommittedStateAccumulator() {
  const accumulatorRef = useRef<Map<string, TripPlannerCommittedStateEntry>>(new Map())

  useEventSubscription('trip_planner_committed_state', event => {
    event.entries.forEach(entry => accumulatorRef.current.set(entry.key, entry))
    pruneExpiredEntries(accumulatorRef.current)
  })

  const clearCommittedState = useCallback(() => {
    accumulatorRef.current.clear()
  }, [])

  useEventSubscription('reset_chat_state', clearCommittedState)

  // Critical lifecycle rule: a pushed entry must keep reappearing in every snapshot build until
  // its own expiresAt has actually passed -- there is no "clear on send" or "after N turns"
  // trigger. This is the one read path, so it both prunes (deletes) and collects in the same
  // pass: an expired entry is safe to actually delete here, since svc-trip will independently
  // treat the same entry as TTL-expired in its own stored copy the moment it stops appearing, per
  // its own isExpiredAt check at render time.
  const collectLiveCommittedStateEntries = useCallback((): Array<TripPlannerCommittedStateEntry> => {
    pruneExpiredEntries(accumulatorRef.current)
    return Array.from(accumulatorRef.current.values())
  }, [])

  return { collectLiveCommittedStateEntries, clearCommittedState }
}
